<template>
  <div class="chat-container">
    <!-- Messages Section -->
    <div class="messages" ref="messagesContainer">
      <div v-for="message in messages" :key="message.id" class="message" >
        <!-- User Messages -->
        <div v-if="message.role === 'user'" class="message-content user-message">
          <div class="user-bubble">
            <div class="user-bubble-message" v-html="renderMarkdown(message.content)"></div>
            <div
              v-for="(fileData) in getFilePreviews(message.files)"
              :key="fileData.original_name"
              class="file-preview"
              :style="fileData.preview ? {backgroundColor: 'transparent !important'} : ''"
            >
              <a v-if="fileData.preview" :href="fileData.preview" target="_blank"><img
                  :src="fileData.preview"
                  alt="Image Preview"
                  class="file-icon-bubble"
              /></a>
              
              <FontAwesomeIcon v-else :icon="fileData.icon" class="file-icon" />
              <div class="file-info" v-if="!fileData.preview">
                <p class="file-name">{{ fileData.original_name }}</p>
                <p class="file-type">{{ fileData.mime_type || 'File' }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Assistant Messages -->
        <div v-else-if="message.role === 'assistant'" class="message-content assistant-message">
          <div class="agent-bubble">
            <div v-html="renderMarkdown(message.content)"></div>
          </div>
        </div>
      </div>
      <div v-if="blockSend" class="message">
      <div class="message-content assistant-message">
        <div class="agent-bubble loading">
          Typing<span>{{ typingDots }}</span>
        </div>
      </div>
    </div>
    <div ref="bottomAnchor"></div> 

    </div>

    <!-- Input Section -->
    <div class="input-container"   
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop">
    <div class="textarea-background">
      <div class="file-preview-container">
          <div
          v-for="(fileData, index) in filePreviews"
          :key="fileData.file.name"
          class="file-preview"
          :style="fileData.preview ? {backgroundColor: 'transparent !important'} : ''"
        >
          <img
            v-if="fileData.preview"
            :src="fileData.preview"
            alt="Image Preview"
            class="image-upload-icon"
          />
          <FontAwesomeIcon v-else :icon="fileData.icon" class="file-icon" />
          <div class="file-info">
            <p class="file-name">{{ fileData.file.name }}</p>
            <p class="file-type">{{ fileData.file.type || 'File' }}</p>
          </div>
          <button @click="clearSelectedFile(index)" class="close-button">✕</button>
        </div>
      </div>
 


    <textarea
      @keydown.enter="sendMessage"
      name="message"
      placeholder="Type your message here..."
      v-model="userMessage"
      rows="3"
    ></textarea>
    <!-- File Upload -->
    <label for="file-upload" class="file-upload-label" >
      <PaperClipIcon class="paperclip-icon" />
    </label>
    <input
      id="file-upload"
      type="file"
      ref="fileInput"
      class="hidden"
      @change="handleFileUpload"
      multiple
    />
  </div>
  <PaperPlaneIcon :disabled="blockSend" :class="getSendClass()" @click.prevent.stop="sendMessage()" />
</div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { marked } from 'marked';
import PaperPlaneIcon from './icons/PaperPlaneIcon.vue';
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css'; // Or choose your preferred theme
import DOMPurify from 'dompurify';
import PaperClipIcon from './icons/PaperClipIcon.vue';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faFile,
  faFileImage,
  faFilePdf,
  faFileVideo,
  faFileWord,
  faFileExcel,
  faFileArchive,
  faFileAlt,
} from '@fortawesome/free-solid-svg-icons';
library.add(faFile, faFileImage, faFilePdf, faFileVideo, faFileWord, faFileExcel, faFileArchive, faFileAlt);


export default {
  name: "SliderConversation",
  data(){
    return{
      userMessage: '',
      blockSend: false,
      typingDots: '', // Initialize here
      selectedFiles: [],
    }
  },
  components: {
    PaperPlaneIcon,
    PaperClipIcon,  
    FontAwesomeIcon
  },
  computed: {
    ...mapGetters(['taskConversation']), // Maps the Vuex getter for the conversation
    messages() {
      return this.taskConversation?.messages || []; // Ensures fallback to an empty array if messages are undefined
    },
    filePreviews() {
      const fileArray = Array.isArray(this.selectedFiles)
        ? this.selectedFiles
        : [this.selectedFiles].filter(Boolean);

      return fileArray.map((file) => {
        const isValidFile = file instanceof File || file instanceof Blob;

        // ✅ Only create previews for image files
        const isImage = file?.type?.startsWith("image/");
        const preview = isValidFile && isImage ? URL.createObjectURL(file) : null;

        return {
          file,
          preview, // ✅ PDFs and CSVs will now have `null`
          icon: this.getFileIcon(file), // ✅ PDFs should now show FontAwesome
          original_name: file?.name || "Unknown File",
          mime_type: file?.type || "unknown",
        };
      });
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  },
  watch: {
    blockSend(newValue) {
      if (newValue) {
        this.startTypingAnimation(); // Start animation when blockSend is true
      } else {
        this.stopTypingAnimation(); // Stop animation when blockSend is false
      }
    },
    messages() {
      this.scrollToBottom(); // Scroll when messages update
    }
  },
  methods: {
    async createResizedPreview(file, maxWidth = 800, maxHeight = 800) {
      return new Promise((resolve) => {
  const img = new Image();
  img.src = URL.createObjectURL(file);

  img.onload = () => {
    URL.revokeObjectURL(img.src); // Clean up to prevent memory leaks

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    let width = img.width;
    let height = img.height;

    if (width > maxWidth || height > maxHeight) {
      const aspectRatio = width / height;
      if (width > height) {
        width = maxWidth;
        height = maxWidth / aspectRatio;
      } else {
        height = maxHeight;
        width = maxHeight * aspectRatio;
      }
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);

    // Get preview URL from resized image
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error("Failed to generate blob from canvas");
        return resolve(null); // ✅ Prevents crash, just return null
      }

      resolve({
        preview: URL.createObjectURL(blob), // ✅ Only create if blob exists
        original_name: file.name,
        mime_type: file.type,
      });
    }, "image/jpeg", 0.7);
  };

  img.onerror = () => {
    console.error("Image failed to load:", file);
    resolve(null); // ✅ Prevents crash if image fails to load
  };
});
},
    getFilePreviews(files) {
      const fileArray = Array.isArray(files) ? files : [files].filter(Boolean);

      return fileArray.map((file) => {
        try {
          const hasPublicUrl = typeof file.public_url === "string" && file.public_url.includes("storage.googleapis.com");
          
          return {
            file,
            preview: hasPublicUrl 
              ? file.public_url  // ✅ Use GCP URL directly if available
              : (file.type?.startsWith("image/") ? URL.createObjectURL(file) : null), // Otherwise, create object URL
            icon: this.getFileIcon(file),
            original_name: file.original_name || file.name,
            mime_type: file.mime_type || file.type,
          };
        } catch (error) {
          console.error("Error processing file preview:", file, error);
          return {
            file,
            preview: null, // Prevent crashing on invalid files
            icon: this.getFileIcon(file),
            original_name: file.original_name || file.name,
            mime_type: file.mime_type || file.type,
          };
        }
      });
    },
    async handleFileUpload(event) {
      const files = Array.from(event.target.files); // Convert FileList to Array
      const previews = await Promise.all(files.map(this.createResizedPreview));

      this.selectedFiles = [...this.selectedFiles, ...files]; // Append new files
      this.filePreviews = [...this.filePreviews, ...previews]; // Store resized versions for preview

    },
    clearSelectedFile(index) {
      this.selectedFiles.splice(index, 1); // Remove file at the given index
    },
    onDragOver(event) {
    // Add a class for visual feedback when dragging files
    event.currentTarget.classList.add('drag-over');
  },
  onDragLeave(event) {
    // Remove the visual feedback class
    event.currentTarget.classList.remove('drag-over');
  },
  onDrop(event) {
    // Handle dropped files
    const files = Array.from(event.dataTransfer.files);
    this.selectedFiles = [...this.selectedFiles, ...files];
    // Remove the visual feedback class
    event.currentTarget.classList.remove('drag-over');
  },
  getFileIcon(file) {
  const mimeType = file.type || file.mime_type || '';
  const extension = file.name?.split('.').pop()?.toLowerCase() || file.original_name?.split('.').pop()?.toLowerCase() || '';

  const iconMap = {
    pdf: 'fa-file-pdf',
    image: 'fa-file-image',
    video: 'fa-file-video',
    excel: 'fa-file-excel',
    word: 'fa-file-word',
    archive: 'fa-file-archive',
    default: 'fa-file-alt',
  };

  if (mimeType.includes('pdf') || extension === 'pdf') return iconMap.pdf;
  if (mimeType.includes('image') || ['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return iconMap.image;
  if (mimeType.includes('video') || ['mp4', 'avi', 'mkv'].includes(extension)) return iconMap.video;
  if (mimeType.includes('spreadsheet') || ['xls', 'xlsx'].includes(extension)) return iconMap.excel;
  if (mimeType.includes('msword') || ['doc', 'docx'].includes(extension)) return iconMap.word;
  if (mimeType.includes('archive') || ['zip', 'rar', '7z'].includes(extension)) return iconMap.archive;

  return iconMap.default; // Default icon for unsupported types
  },
        scrollToBottom() {
        this.$nextTick(() => {
          const bottomAnchor = this.$refs.bottomAnchor;
          if (bottomAnchor) {
            bottomAnchor.scrollIntoView({ behavior: "smooth" }); // Smooth scrolling
          } else {
            console.error("Bottom anchor not found for scrolling.");
          }
        });
      },
      async sendMessage() {
        if ((!this.userMessage && !this.selectedFiles.length && !this.userMessage.trim()) || this.blockSend) {
          return; // Prevent sending if already blocked or message is empty
        }

        this.blockSend = true;

        const userMessageContent = this.userMessage.trim(); // Save the message content
        const userMessage = {
          id: Date.now(), // Unique ID for the message
          role: 'user',
          content: userMessageContent,
          files: this.selectedFiles || []
        };
        
        this.messages.push(userMessage); // Push user's message to the messages array
        this.scrollToBottom(); // Scroll immediately after pushing the user's message
        this.userMessage = ''; // Clear the input field

        try {
          const formData = new FormData();
          formData.append('message', userMessageContent);

          // Attach files to the form data
          this.selectedFiles.forEach((file) => {
            formData.append('files', file)
          });

          this.selectedFiles = [];
          const response = await this.$store.dispatch('sendMessage',  formData); // Use saved message content
          this.scrollToBottom(); // Scroll immediately after pushing the user's message
          if (response) {
            const assistantMessage = {
              id: Date.now() + 1, // Ensure unique ID
              role: 'assistant',
              content: response
            };
            this.messages.push(assistantMessage); // Add assistant's response
            this.scrollToBottom(); // Scroll after assistant's response
          }
        } catch (error) {
          console.error('Error while sending message:', error);
        } finally {
          this.blockSend = false; // Allow sending again after the API call completes
        }
    },
    renderMarkdown(content) {
      const html = marked(content, {
        gfm: true,
        breaks: true,
        langPrefix: 'language-',
        highlight: (code, lang) => {
          const validLang = hljs.getLanguage(lang) ? lang : 'plaintext';
          return hljs.highlight(code, { language: validLang }).value;
        }
  });

  // Add manual highlighting for rendered elements
  this.$nextTick(() => {
    document.querySelectorAll('pre code').forEach((block) => {
      hljs.highlightElement(block);
    });
  });

  return DOMPurify.sanitize(html);
},
    getSendClass(){
      return this.blockSend ? "send-message-icon-blocked" : "send-message-icon";
    },
    startTypingAnimation() {
      let dotCount = 0;
      this.typingInterval = setInterval(() => {
        dotCount = (dotCount + 1) % 4; // Cycle through 0, 1, 2, 3
        this.typingDots = '.'.repeat(dotCount); // Add 1-3 dots
      }, 500); // Adjust speed of the dots
    },
    stopTypingAnimation() {
      clearInterval(this.typingInterval); // Clear the interval
      this.typingDots = ''; // Reset dots
    }
  }
};
</script>

<style scoped>
.paperclip-icon{
  width: 20px;
  height: 20px;
}
.file-upload-label{
  cursor: pointer;
  margin-right: auto;
}
.file-preview-container {
  display: flex;
  gap: 1rem; /* Space between file previews */
  overflow-x: auto; /* Enable horizontal scrolling */
  padding: 0.5rem 0; /* Optional: Add some padding for the scroll area */
}


.file-preview {
  display: flex;
  align-items: center;
  gap: 1rem; /* Space between icon and text */
  padding: 0.5rem 1rem;
  background-color: #333; /* Dark background */
  border-radius: 8px;
  color: #fff;
  width: 250px; /* Fixed width for each file preview */
  flex-shrink: 0; /* Prevent the previews from shrinking */
  overflow: hidden; /* Hide overflowing text */
  margin-bottom: 10px;
}

.file-icon {
  width: 25px;
  height: 25px;
  color: #ccc;
  flex-shrink: 0; /* Prevent the icon from shrinking */
}
.file-icon-bubble{
  width: 200px;
  color: #ccc;
  flex-shrink: 0; /* Prevent the icon from shrinking */
  background-color: white;
}
.image-upload-icon{
  width: 50px;
  height: 50px;
  color: #ccc;
  flex-shrink: 0; /* Prevent the icon from shrinking */
}
.file-info {
  flex-grow: 1; /* Allow text to take available space */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipsis; /* Add ellipsis for long filenames */
  white-space: nowrap; /* Keep text in a single line */
}

.file-name {
  font-weight: bold;
  margin: 0;
  font-size: 14px;
}

.file-type {
  font-size: 12px;
  color: #aaa;
}

.close-button {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
}



.textarea-background{
  width: 800px;
  flex-direction: column;
}

::v-deep pre code.hljs {
  /* highlight.js should now apply */
}

.send-message-icon-blocked{
  width: 30px;
  opacity: 0.5; /* Dim the icon to indicate it's disabled */
  cursor: not-allowed !important;
  pointer-events: none; /* Prevent any interaction */

}
.send-message-icon{
  width: 30px;
  cursor: pointer;
}

.chat-container {
  display: flex;
  flex-direction: column;
  gap: 1rem; /* Adds space between messages and input */
  height: calc(100vh - 50px); /* Full height of the viewport */
  padding: 16px;
}
.messages {
  flex: 1; /* Allows the messages to take up available vertical space */
  overflow-y: auto; /* Enables scrolling for overflowing messages */
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.message-content {
  display: flex;
  max-width: 70%;
}

.user-message {
  justify-content: flex-end; /* Aligns user messages to the right */
  margin-left: auto;
}

.assistant-message {
  justify-content: flex-start; /* Aligns assistant messages to the left */
}

.agent-bubble {
  position: relative;
  padding: 12px 25px; /* Space inside the bubble */
  border-radius: 16px; /* Rounded corners */
  background-color: var(--black-colo-08); /* Inner background color */
  color: var(--white-color); /* Text color */

  /* Gradient border effect */
  border: 1px solid transparent; /* Border to create the effect */
  background-clip: padding-box, border-box; /* Clip the inner and border layers */
}
.agent-bubble div{
  padding-left: 20px;
}
.agent-bubble::before {
  content: '';
  position: absolute;
  inset: -1px; /* Ensure the border is exactly 1px outside the bubble */
  border-radius: 16px; /* Match the bubble's shape */
  background: linear-gradient(to top left,#42c88d, transparent 50%);
  -webkit-mask: linear-gradient(#fff, #fff); /* Mask the gradient to fit */
  mask: linear-gradient(#fff, #fff);
  z-index: -1; /* Keep it behind the content */
}

.user-bubble {
  position: relative;
  padding: 12px 25px; /* Space inside the bubble */
  border-radius: 16px; /* Rounded corners */
  background-color: var(--black-colo-08); /* Inner background color */
  color: var(--white-color); /* Text color */

  /* Gradient border effect */
  border: 1px solid transparent; /* Border to create the effect */
  background-clip: padding-box, border-box; /* Clip the inner and border layers */
}
.user-bubble-message{
  margin-bottom: 10px;
}

.user-bubble::before {
  content: '';
  position: absolute;
  inset: -1px; /* Ensure the border is exactly 1px outside the bubble */
  border-radius: 16px; /* Match the bubble's shape */
  background: linear-gradient(to top right,#00D9F5, transparent 50%);
  -webkit-mask: linear-gradient(#fff, #fff); /* Mask the gradient to fit */
  mask: linear-gradient(#fff, #fff);
  z-index: -1; /* Keep it behind the content */
}


p {
  margin: 0;
  font-size: 14px;
}

.input-container {
  display: flex;
  justify-content: center; /* Centers the input field horizontally */
  gap: 5px;
}

.input-box {
  width: 100%; /* Input takes the full width of the container */
  max-width: 800px; /* Optional: Set a maximum width for the input */
  padding: 12px 16px; /* Space inside the input */
  border-radius: 8px;
  border: 1px solid #ccc; /* Border color */
  font-size: 14px;
  outline: none;
}

.input-box:focus {
  border-color: #42c88d; /* Highlighted border when focused */
}

/* Style the scrollbar */
.messages::-webkit-scrollbar {
  width: 8px; /* Width of the scrollbar */
}

.messages::-webkit-scrollbar-track {
  background: #1b1b1b; /* Dark background for the track */
  border-radius: 8px; /* Rounded corners for the track */
}

.messages::-webkit-scrollbar-thumb {
  background: #333; /* Darker gray for the thumb */
  border-radius: 8px; /* Rounded corners for the thumb */
}

.messages::-webkit-scrollbar-thumb:hover {
  background: #555; /* Slightly lighter gray on hover */
}

/* Apply scrollbar styles to .file-preview-container using ::v-deep */
.file-preview-container::-webkit-scrollbar {
  height: 8px !important; /* Ensure the height is applied */
}

 .file-preview-container::-webkit-scrollbar-track {
  background: #1b1b1b !important; /* Dark background for the track */
  border-radius: 8px !important; /* Rounded corners */
}

 .file-preview-container::-webkit-scrollbar-thumb {
  background: #333 !important; /* Thumb color */
  border-radius: 8px !important; /* Rounded corners */
}

.file-preview-container::-webkit-scrollbar-thumb:hover {
  background: #555 !important; /* Slightly lighter gray on hover */
}

.agent-bubble.loading {
  font-style: italic;
  color: #aaa;
  background-color: #333;
  width: 6rem;
}

.agent-bubble.loading span {
  animation: blink 1.5s infinite;
}

@keyframes blink {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .message-content{
    max-width: 100% !important;
  }
}
</style>
